import React, { useState, useEffect, useRef } from "react";
import "./voiceNavigation.css";
import Lottie from "lottie-web";
import {
  selectIsDictionaryActive,
  selectIsVoiceNavigationActive,
} from "../../reduxSlices/booleanSlice";
import { useSelector } from "react-redux";
import { selectIsDictionaryModalOpen } from "../../reduxSlices/dictionarySlices";
import { useIsMobile } from "../../store/user";

interface TabData {
  General: { command: string; explanation: string }[];
  Navigation: { command: string; explanation: string }[];
  Gestures: { command: string; explanation: string }[];
  Links: { command: string; explanation: string }[];
}

export default function VoiceNavigation() {
  const [isListening, setIsListening] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [listenedText, setListenedText] = useState("");
  const [micFailure, setMicFailure] = useState(false);
  const recordingAnimationRef = useRef(null);
  const isVoiceNavigationActive = useSelector(selectIsVoiceNavigationActive);
  const isDictionaryActive = useSelector(selectIsDictionaryActive);
  const isDictionaryModalOpen = useSelector(selectIsDictionaryModalOpen);
  const isMobile = useIsMobile();

  const [activeTab, setActiveTab] = useState<keyof TabData>("General");

  useEffect(() => {
    if (recordingAnimationRef.current) {
      const loader = Lottie.loadAnimation({
        container: recordingAnimationRef.current,
        renderer: "svg",
        loop: true,
        autoplay: true,
        animationData: require("../../components/lottie/mic.json"),
      });
      return () => loader.destroy();
    }
  });
  //   useEffect(() => {
  //     if ('permissions' in navigator) {
  //       navigator.permissions.query({ name: 'microphone' as PermissionName }).then((result) => {
  //         console.log('Mic permission:', result.state);

  //         if (result.state !== 'granted') {
  //           setMicFailure(true);
  //         } else {
  //           setMicFailure(false);
  //         }
  //         result.onchange = () => {
  //           console.log('Mic permission changed:', result.state);
  //           setMicFailure(result.state !== 'granted');
  //         };
  //       });
  //     }
  //   }, []);

  // Data for each tab
  const tabData = {
    General: [
      {
        command: "Open widget",
        explanation:
          "Opens or reveals the accessibility widget or tool on the webpage.",
      },
      {
        command: "Close widget",
        explanation:
          "Closes or hides the accessibility widget or tool from the webpage.",
      },
      {
        command: "Enable [function name]",
        explanation: "Enable [ function name ] activates the function.",
      },
      {
        command: "Disable [function name]",
        explanation: "Disable [ function name ] deactivates the function.",
      },
    ],
    Navigation: [
      {
        command: "Go to home page",
        explanation:
          "Navigates the user back to the main homepage of the website.",
      },
      {
        command: "Refresh page",
        explanation:
          "Reloads the current page to reflect any updates or changes.",
      },
      {
        command: "Scroll [up/down/left/right]",
        explanation:
          "Moves the page in the specified direction for easier navigation.",
      },
      {
        command: "Next page / Previous page",
        explanation:
          "Navigates to the next or previous page in the browsing sequence.",
      },
    ],
    Gestures: [
      {
        command: "Zoom in",
        explanation:
          "Increases the page magnification for better readability and visibility.",
      },
      {
        command: "Zoom out",
        explanation:
          "Decreases the page magnification to show more content on the screen.",
      },
    ],
    Links: [
      {
        command: "Click on [number]",
        explanation:
          "Selects the specified numbered element or option on the screen.",
      },
      {
        command: "Click [button name]",
        explanation:
          "Activates the specified button to perform its assigned action.",
      },
      {
        command: "Select [option name]",
        explanation: "Chooses the specified option from a list or menu.",
      },
      {
        command: 'Open menu "/"Close menu',
        explanation:
          "Expands or collapses the menu for navigation or selection.",
      },
    ],
  };

  window.addEventListener("message", (event) => {
    const { type, payload } = event.data || {};
    if (type === "VOICE_COMMAND") {
      const receivedCommand = payload.command;
      const commandDisplay = document.getElementById("received-command");
      if (commandDisplay) {
        commandDisplay.textContent = `Received: "${receivedCommand}"`;
      }
      setListenedText(receivedCommand);
    }
  });

  return (
    <div
      className={`${isDictionaryModalOpen ? "z-[10]" : "z-[99]"} fixed bg-language-background  ${!showHelp ? "bottom-4 left-1/2 " : "flex items-center  justify-center top-0 left-0 right-0 bottom-0 "}`}
    >
      {showHelp && (
        <div
          className={`${isMobile ? "w-[90%]" : "w-[35.125rem]"} bg-white rounded-voice-help h-[25.25rem]   drop-shadow-voice-help-shadow pt-4 pb-4 flex flex-col justify-center ${micFailure ? "mb-[4rem]" : "mb-[3rem]"}`}
        >
          <div className="flex flex-row items-center justify-center w-[100%] font-[700] text-languageFontSize">
            <div className="flex flex-row items-center justify-between w-[100%] pl-[1.64rem] pr-[1.64rem]">
              <div />
              <div className={`${isMobile ? "text-primary" : ""} font-[700]`}>
                Voice navigation Commands
              </div>
              <div className="flex flex-row w-[1.75rem] items-center justify-center py-1 hover:bg-setting-card h-fit cursor-pointer focus:bg-toggle-background searchIconPress ">
                <div
                  style={{
                    backgroundImage: `url(${"/language/closeBlack.svg"})`,
                  }}
                  className="w-[1rem] h-[1rem] cursor-pointer bg-no-repeat bg-center bg-contain"
                  onClick={() => {
                    setShowHelp(false);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="flex w-[100%] justify-center">
            <div
              className={`${isMobile ? "rounded-voice-helpMobile" : "rounded-[0.5rem] "} flex justify-between flex-row gap-1 mt-4 bg-toggle-background w-[90%] p-0.5`}
            >
              {Object.keys(tabData).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as keyof TabData)}
                  className={`${isMobile ? "text-quaternary rounded-voice-helpMobile" : "text-voice-help-tab rounded-[0.5rem]"} w-[8.625rem] h-[2.125rem] font-[700]  trasnition-all duration-300  ${
                    activeTab === tab
                      ? "bg-white  drop-shadow-voice-help-header-shadow"
                      : "border-transparent text-toggle-place-holder "
                  } focus:outline-none`}
                >
                  <span
                    className={` transition-all duration-300 ${activeTab === tab ? "textGradient" : "text-toggle-place-holder hover:text-voice-help-title-hover"}`}
                  >
                    {tab}
                  </span>
                </button>
              ))}
            </div>
          </div>
          {/* Tab Content Section */}
          <div className="flex h-[100%] w-[100%] items-center justify-center  overflow-auto keyTabContainer">
            <div className=" w-[90%] h-[100%]">
              <div className="w-[100%] flex flex-col items-center justify-center">
                <div className="flex justify-between w-[100%] pl-[2.38rem] pt-[1.31rem]">
                  <div
                    className={`${isMobile ? "text-primary" : "text-secondary"} w-[50%]  font-[700] leading-[1.375rem]`}
                  >
                    Command
                  </div>
                  <div
                    className={`${isMobile ? "text-primary" : "text-secondary"} w-[50%] font-[700] leading-[1.375rem]`}
                  >
                    Explanantion
                  </div>
                </div>
                <div className="mt-[1.25rem]">
                  {tabData[activeTab].map((item, index) => (
                    <div
                      className={`flex pt-[0.45rem] pb-[0.45rem] ${index + 1 === tabData[activeTab].length ? "border-b-0" : "border-b"}`}
                    >
                      <div
                        className={`${isMobile ? "text-primary" : "text-secondary"} w-[45%]  font-quarternary`}
                      >
                        {item.command}
                      </div>
                      <div
                        className={`${isMobile ? "text-primary" : "text-secondary"} w-[55%]  font-primary`}
                      >
                        {item.explanation}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        className={`${
          isDictionaryActive && isVoiceNavigationActive
            ? isMobile && showHelp
              ? "bottom-[3rem]"
              : isMobile
                ? "bottom-[12rem]"
                : "bottom-[7.19rem]"
            : isMobile && showHelp
              ? "bottom-[3rem]"
              : isMobile
                ? "bottom-[5.5rem]"
                : "bottom-[2.19rem]"
        } 
                ${isMobile ? "w-[100%] pl-[2rem] pr-[1rem]" : "w-[31.8125rem] "} 
                fixed left-1/2 transform -translate-x-1/2 flex justify-center z-[99]`}
      >
        {/* Didn't hear that. Try again. */}
        <div
          className={`bg-white relative ${isMobile ? "rounded-voice-mobile-border-radius pr-[1rem] " : "rounded-voice-border-radius pr-[1.5rem] "} shadow-lg  w-[100%]  border-r-voice-border border-t-voice-border border-b-voice-border  border-widget-background flex items-center justify-between ${micFailure ? "h-voice-not-recognise-height" : "h-voice-navigation-height"}`}
        >
          <div className={`${isMobile ? "pl-[3.5rem]" : "pl-[8.427rem]"}`}>
            {micFailure ? (
              <div className="flex flex-col  items-start justify-center">
                <div
                  className={`${isMobile ? "text-primary text-wrap" : "text-voice-font-size"} text-widget-background font-[700]`}
                >
                  Didn't hear that. Try again.
                </div>
                <div
                  className={`${isMobile ? "text-primary text-wrap " : "text-voice-help-tab text-wrap w-[70%]"} text-widget-background font-[500]  `}
                >
                  Click on microphone to continue
                  {isMobile ? "" : " or press Ctrl + alt + V"}{" "}
                </div>
              </div>
            ) : (
              <div
                className={`${isMobile ? "text-[0.875rem] text-wrap" : "text-languageFontSize"} text-widget-background font-bold`}
              >
                {listenedText.length > 0
                  ? listenedText
                  : isListening
                    ? "Listening..."
                    : "Starting voice navigation..."}
              </div>
            )}
          </div>
          <div
            style={{ backgroundImage: `url(${"/voice/help.svg"})` }}
            className="w-[2.125rem] h-[2.125rem] cursor-pointer bg-no-repeat bg-center bg-contain"
            onClick={() => {
              setShowHelp(true);
            }}
          />
          <div
            className={`${isMobile ? "left-[-2rem]" : "left-[-1rem]"} border-4 absolute  border-voice-background flex items-center justify-center  bg-white rounded-full  h-voicee-mic-height w-voice-mic-width drop-shadow-voice-mic-shadow
                          ${micFailure ? `top-[-0.18rem] left-[-1rem]` : `top-[-1rem] left-[-1rem]`}`}
            onClick={() => {
              setListenedText("Recognized text : Scroll down ");
            }}
          >
            {isListening && !micFailure && (
              <div
                ref={recordingAnimationRef}
                className="absolute top-0 left-0 w-full h-full z-[5]"
              />
            )}
            {/* <img src="/voice/mic.svg" className={`rounded-full cursor-pointer bg-white z-[50] p-2 ${micFailure ? "":"shadow-voice-mic-shadow hover:shadow-voice-mic-shadowHover "}   `} /> //todo*/}
            <div
              style={{ backgroundImage: `url(${"/voice/mic.svg"})` }}
              className={`w-[55%] h-[55%] rounded-full cursor-pointer bg-white z-[50] p-2 ${micFailure ? "" : "shadow-voice-mic-shadow hover:shadow-voice-mic-shadowHover "} bg-no-repeat bg-center bg-contain`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}


